import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  DestinationPicker,
  DestinationScopeRow,
  FollowUpPhoneSelect,
  ManualDestinationForm,
  type ManualDestinationFormData,
} from '@/components/destinations';
import { SaveBehaviorToggle } from '@/components/destinations/SaveBehaviorToggle';
import { DestinationSearchPanel } from '@/components/destinations/search/DestinationSearchPanel';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { useDestinations } from '@/hooks/useDestinations';
import { useProviderServiceDestinationsUpdateMutation } from '@/services/api/generated/providerDestinationsService';
import type {
  DestinationOption,
  DestinationScope,
  SaveMode,
} from '@/types/provider-destinations';
import {
  DestinationChannelType,
  DestinationScope as DestinationScopeEnum,
  ResearchStatus,
  SaveMode as SaveModeEnum,
  UI_MANUAL_EVIDENCE_TO_API_SOURCE,
  VerificationStatus,
} from '@/types/provider-destinations';

import type { WizardScopeSelection } from './useCreateRequestWizard';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DestinationSelectionStepProps {
  selectedScopes: WizardScopeSelection[];
  primaryProviderServiceId: string | null | undefined;
  billingProviderServiceId: string | null;
  billingOrganizationName: string | null;
  providerName: string;
  providerCity?: string;
  searchScope: DestinationScope | null;
  onSetSearchScope: (scope: DestinationScope | null) => void;
  onSetDestination: (
    scope: DestinationScope,
    dest: DestinationOption | null,
  ) => void;
  onSetCreateWithoutDestination: (
    scope: DestinationScope,
    createWithoutDestination: boolean,
  ) => void;
  onSetSaveBehavior: (scope: DestinationScope, mode: SaveMode) => void;
  onSetScopeFollowUpPhone: (scope: DestinationScope, phone: string) => void;
  onHydrateScopeFollowUpPhone: (
    scope: DestinationScope,
    phone: string,
  ) => void;
  hydratedFollowUpPhoneScopes: DestinationScope[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DestinationSelectionStep = ({
  selectedScopes,
  primaryProviderServiceId,
  billingProviderServiceId,
  billingOrganizationName,
  providerName,
  providerCity,
  searchScope,
  onSetSearchScope,
  onSetDestination,
  onSetCreateWithoutDestination,
  onSetSaveBehavior,
  onSetScopeFollowUpPhone,
  onHydrateScopeFollowUpPhone,
  hydratedFollowUpPhoneScopes,
}: DestinationSelectionStepProps) => {
  // Which scope is open for editing (inline picker)
  const [activeScope, setActiveScope] = useState<DestinationScope | null>(null);
  // Which scope has manual entry open
  const [manualScope, setManualScope] = useState<DestinationScope | null>(null);

  const {
    scopes: primaryServiceScopes,
    getScopeDestinations: getPrimaryScopeDestinations,
  } = useDestinations(primaryProviderServiceId ?? '');
  const {
    scopes: billingServiceScopes,
    getScopeDestinations: getBillingScopeDestinations,
  } = useDestinations(billingProviderServiceId ?? '');
  const [setDestinationPreference] =
    useProviderServiceDestinationsUpdateMutation();

  const getProviderServiceIdForScope = useCallback(
    (scope: DestinationScope): string | null => {
      if (scope === DestinationScopeEnum.BILLS && billingProviderServiceId) {
        return billingProviderServiceId;
      }
      return primaryProviderServiceId ?? null;
    },
    [billingProviderServiceId, primaryProviderServiceId],
  );

  const getScopeDestinations = useCallback(
    (scope: DestinationScope) => {
      if (scope === DestinationScopeEnum.BILLS && billingProviderServiceId) {
        return getBillingScopeDestinations(scope);
      }
      return getPrimaryScopeDestinations(scope);
    },
    [
      billingProviderServiceId,
      getBillingScopeDestinations,
      getPrimaryScopeDestinations,
    ],
  );

  const isDifferentBillingRoutingForScope = useCallback(
    (scope: DestinationScope): boolean =>
      scope === DestinationScopeEnum.BILLS &&
      Boolean(billingProviderServiceId) &&
      billingProviderServiceId !== primaryProviderServiceId,
    [billingProviderServiceId, primaryProviderServiceId],
  );

  const billingRoutingLabel =
    billingOrganizationName?.trim() || 'Billing organization';

  const formatManualAddress = (data: ManualDestinationFormData): string => {
    const parts = [data.address_line1];
    if (data.address_line2) parts.push(data.address_line2);
    parts.push(`${data.city}, ${data.state} ${data.postal_code}`);
    return parts.join(', ');
  };

  const toManualDestinationOption = (
    data: ManualDestinationFormData,
  ): DestinationOption => ({
    contact_point_id: '',
    channel_type: data.channel_type,
    value:
      data.channel_type === DestinationChannelType.MAIL
        ? formatManualAddress(data)
        : data.value,
    verification_status: VerificationStatus.UNVERIFIED,
    evidence_summary: 'Manual entry',
  });

  // Auto-populate follow-up phones from the backend once per scope.  The
  // hydration tracking lives in the wizard reducer so it persists across
  // step navigation (this component unmounts when the user leaves the
  // destinations step).  Without the guard the effect would re-fire on
  // every `selectedScopes` change, fighting user edits.
  useEffect(() => {
    selectedScopes.forEach((selection) => {
      if (hydratedFollowUpPhoneScopes.includes(selection.scope)) return;
      const phone = getScopeDestinations(
        selection.scope,
      )?.voiceai_followup_phone;
      if (phone?.trim()) {
        onHydrateScopeFollowUpPhone(selection.scope, phone.trim());
      }
    });
  }, [
    selectedScopes,
    primaryServiceScopes,
    billingServiceScopes,
    getScopeDestinations,
    onHydrateScopeFollowUpPhone,
    hydratedFollowUpPhoneScopes,
  ]);

  const handleSelectDestination = (
    scope: DestinationScope,
    option: DestinationOption,
  ) => {
    onSetDestination(scope, option);
    setActiveScope(null);
    onSetSearchScope(null);
    setManualScope(null);
  };

  const handleSelectNoDestination = (scope: DestinationScope) => {
    onSetCreateWithoutDestination(scope, true);
    setActiveScope(null);
    onSetSearchScope(null);
    setManualScope(null);
  };

  const handleManualSubmit = async (
    scope: DestinationScope,
    data: ManualDestinationFormData,
  ) => {
    try {
      const scopeProviderServiceId = getProviderServiceIdForScope(scope);
      let selectedDestination: DestinationOption =
        toManualDestinationOption(data);

      if (scopeProviderServiceId) {
        const response = await setDestinationPreference({
          providerServiceId: scopeProviderServiceId,
          patchedProviderServiceDestinationsPatchRequest: {
            scope,
            save_mode: data.save_mode,
            manual_destination:
              data.channel_type === DestinationChannelType.MAIL
                ? {
                    channel_type: data.channel_type,
                    address_line1: data.address_line1,
                    address_line2: data.address_line2 || undefined,
                    city: data.city,
                    state: data.state,
                    postal_code: data.postal_code,
                  }
                : {
                    channel_type: data.channel_type,
                    value: data.value,
                  },
            evidence: {
              source_type: UI_MANUAL_EVIDENCE_TO_API_SOURCE[data.evidence_type],
              note: data.evidence_detail?.trim() || undefined,
            },
          },
        }).unwrap();

        const scoped = response.scopes.find((entry) => entry.scope === scope);
        if (scoped?.recommended) {
          selectedDestination = scoped.recommended;
        }
      }

      onSetDestination(scope, selectedDestination);
      onSetSaveBehavior(scope, data.save_mode as SaveMode);
      setManualScope(null);
      setActiveScope(null);
    } catch {
      toast.error('Failed to save manual destination');
    }
  };

  const searchScopeSelection = searchScope
    ? (selectedScopes.find((s) => s.scope === searchScope) ?? null)
    : null;
  const searchScopeServiceId = searchScope
    ? getProviderServiceIdForScope(searchScope)
    : null;

  if (searchScope && searchScopeServiceId) {
    return (
      <div className="space-y-4">
        <DestinationSearchPanel
          mode="inline"
          open
          onOpenChange={() => undefined}
          onBack={() => onSetSearchScope(null)}
          providerServiceId={searchScopeServiceId}
          scope={searchScope}
          providerName={providerName}
          providerCity={providerCity}
          onSelectResult={(opt) => handleSelectDestination(searchScope, opt)}
        />

        {searchScopeSelection && (
          <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3">
            <div className="space-y-3">
              {isDifferentBillingRoutingForScope(searchScope) && (
                <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  Bills are being routed from {billingRoutingLabel}.
                </div>
              )}
              <SaveBehaviorToggle
                value={searchScopeSelection.saveBehavior}
                onChange={(mode) =>
                  onSetSaveBehavior(searchScope, mode as SaveModeEnum)
                }
              />
              <Field>
                <Label>Follow-up phone for Voice AI (optional)</Label>
                <FollowUpPhoneSelect
                  value={searchScopeSelection.followUpPhone}
                  onChange={(phone) =>
                    onSetScopeFollowUpPhone(searchScope, phone)
                  }
                  availablePhones={
                    getScopeDestinations(searchScope)?.available_phones ?? []
                  }
                  placeholder="Select or enter phone number"
                />
              </Field>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selectedScopes.map((sel) => {
        const scopeProviderServiceId = getProviderServiceIdForScope(sel.scope);
        const block = scopeProviderServiceId
          ? getScopeDestinations(sel.scope)
          : undefined;
        const isActive = activeScope === sel.scope;

        return (
          <div key={sel.scope} className="rounded-lg border border-zinc-200">
            <DestinationScopeRow
              scope={sel.scope}
              currentDestination={sel.destination}
              usePlaceholderRequest={sel.createWithoutDestination}
              followUpPhone={sel.followUpPhone}
              coverageStatus={block?.coverage_status ?? 'MISSING'}
              researchStatus={
                block?.research_status ?? ResearchStatus.NOT_STARTED
              }
              scopeBadge={
                isDifferentBillingRoutingForScope(sel.scope)
                  ? billingRoutingLabel
                  : undefined
              }
              onEdit={() => setActiveScope(isActive ? null : sel.scope)}
              className="border-0"
            />

            {/* Inline picker panel */}
            {isActive && scopeProviderServiceId && (
              <div className="border-t border-zinc-100 px-4 py-3">
                {manualScope === sel.scope ? (
                  <ManualDestinationForm
                    onSubmit={(data) => handleManualSubmit(sel.scope, data)}
                    onCancel={() => setManualScope(null)}
                  />
                ) : (
                  <>
                    {isDifferentBillingRoutingForScope(sel.scope) && (
                      <div className="mb-3 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                        Bills destinations shown from {billingRoutingLabel}.
                      </div>
                    )}
                    <DestinationPicker
                      scope={sel.scope}
                      providerServiceId={scopeProviderServiceId}
                      onSelectDestination={(opt) =>
                        handleSelectDestination(sel.scope, opt)
                      }
                      onOpenSearch={() => onSetSearchScope(sel.scope)}
                      onOpenManualEntry={() => setManualScope(sel.scope)}
                      allowNoDestination
                      onSelectNoDestination={() =>
                        handleSelectNoDestination(sel.scope)
                      }
                    />

                    {/* Save behavior toggle */}
                    <div className="mt-3 space-y-3 border-t border-zinc-100 pt-3">
                      <SaveBehaviorToggle
                        value={sel.saveBehavior}
                        onChange={(mode) =>
                          onSetSaveBehavior(sel.scope, mode as SaveModeEnum)
                        }
                      />
                      <Field>
                        <Label>Follow-up phone for Voice AI (optional)</Label>
                        <FollowUpPhoneSelect
                          value={sel.followUpPhone}
                          onChange={(phone) =>
                            onSetScopeFollowUpPhone(sel.scope, phone)
                          }
                          availablePhones={
                            block?.available_phones ?? []
                          }
                          placeholder="Select or enter phone number"
                        />
                      </Field>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Fallback if no providerServiceId */}
            {isActive && !scopeProviderServiceId && (
              <div className="border-t border-zinc-100 px-4 py-3">
                <ManualDestinationForm
                  onSubmit={(data) => handleManualSubmit(sel.scope, data)}
                  onCancel={() => setActiveScope(null)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
